import { ApiError, OrderWithContext } from '@/models';
import {
  GetRewardOffersResponse,
  Reward,
  GetRewardOfferTypesResponse,
  RewardStoreErrorCode,
  GetExtrasCampaignsResponse,
  GetExtrasAchievementsResponse,
  AchievementTitle,
  BagOffersResponse,
  PurchasedReward,
  Achievement,
} from '@/models/reward';
import { AppState } from '@/store';
import { MUTATIONS } from '@/store/reward';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { Store } from 'vuex';
import { AnalyticsService } from '../analytics/analytics.service';

import { ApiService } from '../api/api.service';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { Logger } from '../logger';

export interface RewardService {
  getFavoriteReward(): Reward | undefined;
  getPointsBalance(): number;
  getNumRedeemableRewards(): number;
  getNumRedeemableRewardsStr(): string;
  getProgressPercentage(): number;
  getRewardStarUrl(reward: Reward, hover: boolean): string;
  getRewardOffers(): Promise<any>;
  getBagOffers(): Promise<any>;
  getRewardStoreOfferTypes(): Promise<any>;
  getExtrasAchievements(): Promise<any>;
  getExtrasCampaigns(): Promise<any>;
  getTwitterAchievementUrl(achievementTitle: string, baseUrl: string): string;
  isSessionMError(error: AxiosError): boolean;
  isSparkflyError(error: AxiosError): boolean;
  setShouldShowRefresh(value: boolean): void;
  favoriteReward(offerId: string): Promise<any>;
  unfavoriteReward(offerId: string): Promise<any>;
  purchaseReward(offerId: string): Promise<PurchasedReward | null>;
  purchaseAndApplyReward(offerId: string, order: OrderWithContext): Promise<any>;
  toggleFavoriteStatus(reward: Reward): void;
  clearRewards(): void;
}

export class AppRewardService extends ApiService implements RewardService {
  pendingCalls = 0;

  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, private featureFlags: FeatureFlagsService, analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }
  
  isRewardsStoreOnline(): boolean {
    const flags = this.store.state.featureFlags;

    if (flags.orderingStatus && !flags.orderingStatus.isRewardsExchangeOnline) {
      return false;
    }

    return true;
  }

  async getRewardStoreOfferTypes() {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/offerTypes`)
      .then((res: AxiosResponse<GetRewardOfferTypesResponse>) => {
        this.store.commit(MUTATIONS.SET_REWARDSTORE_OFFERTYPES, res.data);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getRewardStoreOfferTypes', e));
  }

  async getBagOffers(){
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    const cmgIds: string[] = [];
    const order: OrderWithContext | null = this.store.getters.currentOrder;

    if (order && order.order.meals) {
      order.order.meals.forEach(meal => {
        if (meal.entrees && meal.entrees.length > 0) {
          meal.entrees.forEach(entree => {
            cmgIds.push(entree.menuItemId);
          });
        }
        if (meal.sides && meal.sides.length > 0) {
          meal.sides.forEach(side => {
            cmgIds.push(side.menuItemId);
          });
        }
      });
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/bagOffers?CmgIds=${cmgIds.join()}&AvailablePoints=${this.getPointsBalance()}&Channel=web`)
      .then((r: AxiosResponse<BagOffersResponse>) => {
        this.store.commit(MUTATIONS.SET_BAG_OFFERS, r.data.rewardStoreOffers);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
        return r.data;
      })
      .catch(e => this.handleError('getBagOffers', e));
  }

  async getRewardOffers() {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/web`)
      .then((res: AxiosResponse<GetRewardOffersResponse>) => {
        this.store.commit(MUTATIONS.SET_REWARDS, res.data.rewardStoreOffers);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getRewardOffers', e));
  }

  async favoriteReward(offerId: string) {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/favorite/${offerId}`)
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('favoriteReward', e));
  }

  async unfavoriteReward(offerId: string) {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .delete(`/rewardstore/v2/rewardstore/favorite/${offerId}`)
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('unfavoriteReward', e));
  }

  async purchaseReward(offerId: string): Promise<PurchasedReward | null>  {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve(null);
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/offer/${offerId}`)
      .then((r: AxiosResponse<PurchasedReward>) => {
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false)
        return r.data;
      })
      .catch(e => this.handleError('purchaseReward', e));
  }

  async purchaseAndApplyReward(offerId: string, order: OrderWithContext) {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .post(`/rewardstore/v2/rewardstore/offer/${offerId}/${order.order.orderId}`, null, {
        headers: {
          'If-Match': order.etag,
        }
      })
      .then(() => this.store.commit(MUTATIONS.SET_UNREACHABLE, false))
      .catch(e => this.handleError('purchaseAndApplyReward', e));
  }

  async getExtrasCampaigns() {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }

    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/extrasCampaigns`)
      .then((res: AxiosResponse<GetExtrasCampaignsResponse>) => {
        this.store.commit(MUTATIONS.SET_EXTRAS_CAMPAIGNS, res.data.campaigns);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getExtrasCampaigns', e));
  }

  async getExtrasAchievements() {
    await this.featureFlags.ensureFeatureFlagsFetched();
    if (!this.isRewardsStoreOnline()) {
      return Promise.resolve();
    }
    
    return this.httpClient
      .get(`/rewardstore/v2/rewardstore/extrasAchievements`)
      .then((res: AxiosResponse<GetExtrasAchievementsResponse>) => {
        const achievements: Achievement[] = [];
        res.data.achievementYears.forEach(year => {
          year.badges.forEach(badge => achievements.push(badge.achievement));
        });
        this.store.commit(MUTATIONS.SET_EXTRAS_ACHIEVEMENT_YEARS, res.data.achievementYears);
        this.store.commit(MUTATIONS.SET_EXTRAS_ACHIEVEMENTS, achievements);
        this.store.commit(MUTATIONS.SET_TOTAL_EXTRA_POINTS, res.data.totalPointsEarnedFromExtras);
        this.store.commit(MUTATIONS.SET_UNREACHABLE, false);
      })
      .catch(e => this.handleError('getExtrasAchievements', e));
  }

  getPointsBalance(): number {
    return this.store.state.customer.rewardPoints?.currentPointsBalance ?? 0;
  }

  getNumRedeemableRewards(): number {
    const pointsBalance = this.getPointsBalance();

    return this.store.state.reward.rewards
      .filter(r => r.points <= pointsBalance)
      .length;
   }

  getNumRedeemableRewardsStr(): string {
    const numRewards = this.getNumRedeemableRewards();
    return `${numRewards} Reward${numRewards > 1 ? 's' : ''}`;
  }

  getProgressPercentage(): number {
    return this.getFavoriteReward()
      ? (this.getPointsBalance() / this.getFavoriteReward()!.points) * 100
      : 0;
  }

  getFavoriteReward(): Reward | undefined {
    return this.store.state.reward.rewards.find(r => r.isFavorite);
  }

  getRewardStarUrl(reward: Reward, hover: boolean): string {
    if (!reward.isFavorite && hover) {
      return this.store.state.cms.images.icons.rewardStarHover.default;
    }

    return reward.isFavorite
      ? this.store.state.cms.images.icons.rewardStarSelected.default
      : this.store.state.cms.images.icons.rewardStar.default;
  }

  setShouldShowRefresh(value: boolean): void {
    this.store.commit(MUTATIONS.SET_SHOULD_SHOW_REFRESH, value);
  }

  async toggleFavoriteStatus(reward: Reward) {
    // preserve original isFavorite status
    const isFavorite = reward.isFavorite;

    // toggle status for immediate visual feedback
    reward.isFavorite = !reward.isFavorite;

    // toggle status of otherFavoriteReward for immediate visual feedback
    const otherFavoriteReward = this.store.state.reward.rewards.find(r => r.id != reward.id && r.isFavorite);
    if (otherFavoriteReward) {
      otherFavoriteReward.isFavorite = false;
      this.pendingCalls++;
      await this.unfavoriteReward(otherFavoriteReward.id);
      this.pendingCalls--;
    }

    // If it is favorited, unfavorite it
    // If it is not favorited, favorite it
    // If there is another favorited reward, unfavorite it
    this.pendingCalls++;
    isFavorite
      ? await this.unfavoriteReward(reward.id)
      : await this.favoriteReward(reward.id)
    this.pendingCalls--;

    // Don't fetch latest reward offers until after the API calls stop writing to Gigya
    // Otherwise with multiple clicks the user will see unexpected UI behavior.
    if (this.pendingCalls == 0) {
      this.getRewardOffers();
    }
  }

  clearRewards(): void {
    this.store.commit(MUTATIONS.SET_REWARDS, []);
  }

  isSessionMError(error: AxiosError): boolean {
    const errorSet = new Set([
      RewardStoreErrorCode.RewardStoreUnavailable,
      RewardStoreErrorCode.CustomerRecordUnavailable,
      RewardStoreErrorCode.CustomerNotEnrolledInLoyalty,
      RewardStoreErrorCode.SessionMCustomerNotFound,
      RewardStoreErrorCode.InsufficientPoints,
    ]);

    if (error && error.response && error.response.data) {
      return errorSet.has((error as AxiosError<ApiError, any>).response.data.errorCode as RewardStoreErrorCode);
    }

    return false;
  }

  isSparkflyError(error: AxiosError): boolean {
    const errorSet = new Set([
      RewardStoreErrorCode.SparkflyUnavailable,
      RewardStoreErrorCode.SparkflyOfferNotFound,
    ]);

    if (error && error.response && error.response.data) {
      return errorSet.has((error as AxiosError<ApiError, any>).response.data.errorCode as RewardStoreErrorCode);
    }

    return false;
  }

  getTwitterAchievementUrl(achievementTitle: string, baseUrl: string): string {
    switch (achievementTitle.toLowerCase()) {
      case AchievementTitle.BigSipper:
        return baseUrl + '/big-sipper.html';
      case AchievementTitle.FirstToTryBrisket:
        return baseUrl + '/first-to-try-brisket.html';
      case AchievementTitle.InTheBag:
        return baseUrl + '/in-the-bag.html';
      case AchievementTitle.LunchLegend:
        return baseUrl + '/lunch-legend.html';
      case AchievementTitle.NationalGuacDay:
        return baseUrl + '/national-guac-day.html';
      case AchievementTitle.TheDoubleUp:
        return baseUrl + '/the-double-up.html';
      case AchievementTitle.Threepeat:
        return baseUrl + '/three-peat.html';
    }

    return baseUrl;
  }

  private handleError(operationName: string, e: AxiosError) {
    if (e.response) {
      this.store.commit(MUTATIONS.SET_UNREACHABLE, e.response.status >= 400);
    }

    return this.logAndThrowApiError(operationName, e);
  }
}
